import jsPDF from "jspdf";
import html2canvas from "html2canvas";

export default class PDFGenerator {
  constructor() {
    this.pdf = new jsPDF("p", "mm", "a4");
    this.pageWidth = this.pdf.internal.pageSize.getWidth();
    this.pageHeight = this.pdf.internal.pageSize.getHeight();
    this.margin = 20;
    this.currentY = this.margin;

    // ICLR 2026 风格主题色彩
    this.primaryColor = "#000000";
    this.secondaryColor = "#333333";
    this.tertiaryColor = "#666666";
    this.accentColor = "#1a1a1a";
    this.textColor = "#000000";
    this.lightGray = "#f8f8f8";
    this.linkColor = "#0066cc";

    // ICLR 论文标题
    this.paperTitle = "";
    this.shortTitle = "DeepReport";
  }

  // 设置字体和颜色
  setTextStyle(fontSize = 12, color = this.textColor, fontStyle = "normal") {
    this.pdf.setFontSize(fontSize);
    this.pdf.setTextColor(color);
    this.pdf.setFont("helvetica", fontStyle);
  }

  // 检查是否需要新页面
  checkPageBreak(neededHeight = 20) {
    if (this.currentY + neededHeight > this.pageHeight - this.margin) {
      this.pdf.addPage();
      this.currentY = this.margin;
    }
  }

  // 添加标题（ICLR 风格 - 居中、大号、加粗）
  addTitle(text, fontSize = 18) {
    this.checkPageBreak(25);
    this.setTextStyle(fontSize, this.primaryColor, "bold");
    const textWidth = this.pdf.getTextWidth(text);
    const x = (this.pageWidth - textWidth) / 2;
    this.pdf.text(text, x, this.currentY);
    this.currentY += fontSize * 0.35;
    this.paperTitle = text;
  }

  // 添加论文标题（ICLR 风格 - 首页大标题）
  addPaperTitle(text) {
    this.checkPageBreak(35);
    this.setTextStyle(20, this.primaryColor, "bold");
    const maxWidth = this.pageWidth - 2 * this.margin;
    const lines = this.pdf.splitTextToSize(text, maxWidth);

    lines.forEach((line) => {
      const textWidth = this.pdf.getTextWidth(line);
      const x = (this.pageWidth - textWidth) / 2;
      this.pdf.text(line, x, this.currentY);
      this.currentY += 9;
    });

    this.paperTitle = text;
    this.currentY += 5;
  }

  // 添加作者信息（ICLR 风格）
  addAuthors(authors) {
    if (!authors || authors.length === 0) return;

    this.checkPageBreak(15);
    this.setTextStyle(11, this.primaryColor, "normal");

    const authorText = Array.isArray(authors) ? authors.join(", ") : authors;
    const textWidth = this.pdf.getTextWidth(authorText);
    const x = (this.pageWidth - textWidth) / 2;
    this.pdf.text(authorText, x, this.currentY);
    this.currentY += 8;
  }

  // 添加摘要（ICLR 风格）
  addAbstract(abstractText) {
    if (!abstractText) return;

    this.checkPageBreak(50);
    this.currentY += 5;

    // Abstract 标签
    this.setTextStyle(12, this.primaryColor, "bold");
    this.pdf.text("Abstract", this.margin, this.currentY);
    this.currentY += 6;

    // 摘要内容 - 使用斜体
    this.setTextStyle(10, this.textColor, "italic");
    const maxWidth = this.pageWidth - 2 * this.margin;
    const lines = this.pdf.splitTextToSize(abstractText, maxWidth);

    lines.forEach((line) => {
      this.checkPageBreak(5);
      this.pdf.text(line, this.margin, this.currentY);
      this.currentY += 4.5;
    });

    this.currentY += 8;
  }

  // 添加副标题（ICLR 风格 - 章节标题，左对齐）
  addSubTitle(text, fontSize = 14) {
    this.checkPageBreak(20);
    this.currentY += 3;
    this.setTextStyle(fontSize, this.primaryColor, "bold");
    const maxWidth = this.pageWidth - 2 * this.margin;
    const lines = this.pdf.splitTextToSize(text, maxWidth);

    lines.forEach((line, index) => {
      this.pdf.text(line, this.margin, this.currentY);
      this.currentY += fontSize * 0.4;
    });
    this.currentY += 3;
  }

  // 添加带超链接的副标题（ICLR 风格）
  addSubTitleWithLink(text, fontSize = 14, url = null) {
    this.checkPageBreak(20);
    this.currentY += 3;
    this.setTextStyle(fontSize, this.linkColor, "bold");
    const maxWidth = this.pageWidth - 2 * this.margin;
    const lines = this.pdf.splitTextToSize(text, maxWidth);

    lines.forEach((line, index) => {
      const lineY = this.currentY;

      if (url && index === 0) {
        this.pdf.setTextColor(this.linkColor);
        this.pdf.text(line, this.margin, lineY);

        const textWidth = this.pdf.getTextWidth(line);
        this.pdf.link(this.margin, lineY - 4, textWidth, fontSize * 0.5, {
          url: url,
        });
      } else {
        this.pdf.text(line, this.margin, lineY);
      }

      this.currentY += fontSize * 0.4;
    });
    this.currentY += 3;
  }

  // 添加文本（保持两边间距相等，所有行左对齐）
  addText(text, fontSize = 12, maxWidth = null) {
    if (!text) return;

    this.setTextStyle(fontSize);
    const lines = this.pdf.splitTextToSize(
      text,
      maxWidth || this.pageWidth - 2 * this.margin
    );

    lines.forEach((line, index) => {
      this.checkPageBreak(fontSize * 0.6);
      // 所有行都使用相同的左边距，保持两边间距相等
      this.pdf.text(line, this.margin, this.currentY);
      this.currentY += fontSize * 0.6;
    });
  }

  // 添加列表
  addList(items, fontSize = 12) {
    items.forEach((item, index) => {
      this.checkPageBreak(fontSize * 0.8);
      this.setTextStyle(fontSize);
      this.pdf.text(`${index + 1}. ${item}`, this.margin, this.currentY);
      this.currentY += fontSize * 0.8;
    });
  }

  // 添加分隔线（ICLR 风格 - 简洁细线）
  addSeparator() {
    this.currentY += 3;
    this.pdf.setDrawColor(200, 200, 200);
    this.pdf.setLineWidth(0.3);
    this.pdf.line(
      this.margin,
      this.currentY,
      this.pageWidth - this.margin,
      this.currentY
    );
    this.currentY += 8;
  }

  // 添加带样式的文本框
  addStyledBox(title, content, bgColor = this.lightGray) {
    this.checkPageBreak(60);

    // 绘制背景框
    this.pdf.setFillColor(bgColor);
    this.pdf.rect(
      this.margin,
      this.currentY - 5,
      this.pageWidth - 2 * this.margin,
      50,
      "F"
    );

    // 添加标题
    this.setTextStyle(14, this.accentColor, "bold");
    this.pdf.text(title, this.margin + 5, this.currentY + 5);

    // 添加内容
    this.setTextStyle(11, this.textColor, "normal");
    const lines = this.pdf.splitTextToSize(
      content,
      this.pageWidth - 2 * this.margin - 10
    );
    lines.forEach((line, index) => {
      this.pdf.text(line, this.margin + 5, this.currentY + 15 + index * 6);
    });

    this.currentY += 50;
  }

  // 添加表格（ICLR 学术论文风格）
  addTable(data, headers, maxRows = 20) {
    const startX = this.margin;
    const startY = this.currentY;
    const colWidth = (this.pageWidth - 2 * this.margin) / headers.length;
    const rowHeight = 7;

    // 添加表头 - 简洁风格
    this.pdf.setDrawColor(180, 180, 180);
    this.pdf.setLineWidth(0.3);
    this.pdf.setFillColor(245, 245, 245);
    this.pdf.rect(
      startX,
      startY,
      this.pageWidth - 2 * this.margin,
      rowHeight,
      "FD"
    );
    this.pdf.setTextColor(this.textColor);
    this.pdf.setFontSize(9);
    this.pdf.setFont("helvetica", "bold");
    headers.forEach((header, index) => {
      this.pdf.text(header, startX + index * colWidth + 2, startY + 5);
    });

    // 添加表格数据
    this.pdf.setDrawColor(200, 200, 200);
    this.pdf.setTextColor(this.textColor);
    this.pdf.setFont("helvetica", "normal");
    this.pdf.setFontSize(8);
    const displayData = data.slice(0, maxRows);
    for (let rowIndex = 0; rowIndex < displayData.length; rowIndex++) {
      const row = displayData[rowIndex];
      const yPos = startY + (rowIndex + 1) * rowHeight;
      if (yPos > this.pageHeight - this.margin) break;

      // 绘制行边框（交替背景色）
      if (rowIndex % 2 === 1) {
        this.pdf.setFillColor(250, 250, 250);
        this.pdf.rect(
          startX,
          yPos,
          this.pageWidth - 2 * this.margin,
          rowHeight,
          "FD"
        );
      } else {
        this.pdf.rect(
          startX,
          yPos,
          this.pageWidth - 2 * this.margin,
          rowHeight,
          "D"
        );
      }

      row.forEach((cell, cellIndex) => {
        const cellText = String(cell).substring(0, 18);
        this.pdf.text(cellText, startX + cellIndex * colWidth + 2, yPos + 5);
      });
    }

    this.currentY =
      startY + (Math.min(displayData.length, maxRows) + 1) * rowHeight + 5;
  }

  // 添加年份频率图表（ICLR 风格）
  addYearFrequencyChart(yearFreqData) {
    if (!yearFreqData || yearFreqData.length === 0) return;

    this.checkPageBreak(90);
    this.addTitle("Publication Timeline", 14);

    const chartX = this.margin;
    const chartY = this.currentY;
    const chartWidth = this.pageWidth - 2 * this.margin;
    const chartHeight = 50;

    // 绘制背景和边框
    this.pdf.setFillColor(252, 252, 252);
    this.pdf.rect(chartX, chartY, chartWidth, chartHeight, "F");
    this.pdf.setDrawColor(200, 200, 200);
    this.pdf.setLineWidth(0.3);
    this.pdf.rect(chartX, chartY, chartWidth, chartHeight);

    const maxFreq = Math.max(...yearFreqData.map((item) => item.freq || 0));
    if (maxFreq === 0) {
      this.currentY += chartHeight + 15;
      return;
    }

    const barWidth = chartWidth / yearFreqData.length;

    // 绘制柱状图 - 使用学术风格的颜色
    this.pdf.setFillColor(70, 130, 180);
    yearFreqData.forEach((item, index) => {
      const barHeight = (item.freq / maxFreq) * (chartHeight - 10);
      const x = chartX + index * barWidth + barWidth * 0.15;
      const y = chartY + chartHeight - 5 - barHeight;

      this.pdf.rect(x, y, barWidth * 0.7, barHeight, "F");

      if (
        yearFreqData.length <= 10 ||
        index % Math.ceil(yearFreqData.length / 10) === 0
      ) {
        this.pdf.setFontSize(7);
        this.pdf.setTextColor(this.textColor);
        const textWidth = this.pdf.getTextWidth(String(item.year));
        this.pdf.text(
          String(item.year),
          x + (barWidth * 0.7 - textWidth) / 2,
          chartY + chartHeight + 4
        );
      }

      if (barHeight > 8 && barWidth > 12) {
        this.pdf.setFontSize(7);
        this.pdf.setTextColor(this.textColor);
        const freqText = String(item.freq);
        const freqWidth = this.pdf.getTextWidth(freqText);
        this.pdf.text(freqText, x + (barWidth * 0.7 - freqWidth) / 2, y - 2);
      }
    });

    this.currentY += chartHeight + 15;
  }

  // 添加概念频率图表（Top 15）- ICLR 学术论文风格（紧凑）
  addTopConceptsChart(graphData) {
    if (!graphData || !graphData.nodes) return;

    this.checkPageBreak(80);
    this.addTitle("Top 15 Concepts by Degree", 12);

    const topNodes = [...graphData.nodes]
      .sort((a, b) => b.degree - a.degree)
      .slice(0, 15);

    if (topNodes.length === 0) return;

    const chartX = this.margin;
    const chartY = this.currentY;
    const labelWidth = 42;
    const chartWidth = this.pageWidth - 2 * this.margin - labelWidth - 10;
    const rowHeight = 4.5;
    const chartHeight = topNodes.length * rowHeight;

    // 绘制网格线
    this.pdf.setDrawColor(235, 235, 235);
    this.pdf.setLineWidth(0.15);
    const gridCount = 4;
    for (let i = 0; i <= gridCount; i++) {
      const x = chartX + labelWidth + (chartWidth / gridCount) * i;
      this.pdf.line(x, chartY, x, chartY + chartHeight);
    }

    // 绘制基线
    this.pdf.setDrawColor(160, 160, 160);
    this.pdf.setLineWidth(0.25);
    this.pdf.line(
      chartX + labelWidth,
      chartY + chartHeight,
      chartX + labelWidth + chartWidth,
      chartY + chartHeight
    );

    const maxDegree = Math.max(...topNodes.map((node) => node.degree));

    topNodes.forEach((node, index) => {
      const y = chartY + index * rowHeight;
      const barWidth =
        maxDegree > 0 ? (node.degree / maxDegree) * chartWidth : 0;

      // 节点名称
      this.pdf.setFontSize(6);
      this.pdf.setTextColor(80, 80, 80);
      const nodeName =
        node.name.length > 9 ? node.name.substring(0, 9) + ".." : node.name;
      this.pdf.text(nodeName, chartX, y + 3);

      // 条形图
      this.pdf.setFillColor(65, 105, 170);
      this.pdf.rect(
        chartX + labelWidth,
        y + 0.5,
        Math.max(barWidth, 0.5),
        rowHeight - 1,
        "F"
      );

      // 度数值
      this.pdf.setFontSize(5);
      this.pdf.setTextColor(100, 100, 100);
      if (barWidth > 5) {
        this.pdf.text(
          String(node.degree),
          chartX + labelWidth + barWidth + 1,
          y + 3
        );
      }
    });

    // X轴标签
    this.pdf.setFontSize(5);
    this.pdf.setTextColor(130, 130, 130);
    for (let i = 0; i <= gridCount; i++) {
      const value = Math.round((maxDegree / gridCount) * i);
      const x = chartX + labelWidth + (chartWidth / gridCount) * i;
      this.pdf.text(String(value), x - 2, chartY + chartHeight + 3);
    }

    this.currentY += chartHeight + 8;
  }

  // 添加概念社区图表（ICLR 学术论文风格 - 紧凑堆叠条形图）
  addCommunityChart(btnList) {
    if (!btnList || btnList.filter((btn) => btn.type > 0).length === 0) return;

    this.checkPageBreak(60);
    this.addTitle("Concept Communities", 12);

    const validCommunities = btnList.filter((btn) => btn.type > 0);
    const totalCount = validCommunities.reduce((sum, btn) => sum + btn.type, 0);

    if (totalCount === 0) return;

    // 绘制堆叠条形图
    const chartX = this.margin;
    const chartY = this.currentY;
    const chartWidth = this.pageWidth - 2 * this.margin;
    const barHeight = 8;

    // 绘制条形图边框
    this.pdf.setDrawColor(180, 180, 180);
    this.pdf.setLineWidth(0.2);
    this.pdf.rect(chartX, chartY, chartWidth, barHeight);

    let currentX = chartX;
    validCommunities.forEach((btn) => {
      const proportion = btn.type / totalCount;
      const segmentWidth = proportion * chartWidth;

      this.pdf.setFillColor(btn.color);
      this.pdf.rect(currentX, chartY, segmentWidth, barHeight, "F");

      if (segmentWidth > 12) {
        this.pdf.setFontSize(6);
        this.pdf.setTextColor(255, 255, 255);
        this.pdf.setFont("helvetica", "bold");
        const percentText = `${(proportion * 100).toFixed(0)}%`;
        const textWidth = this.pdf.getTextWidth(percentText);
        this.pdf.text(
          percentText,
          currentX + (segmentWidth - textWidth) / 2,
          chartY + barHeight / 2 + 1.5
        );
      }

      currentX += segmentWidth;
    });

    this.currentY += barHeight + 4;

    // 绘制图例（紧凑单行）
    const legendY = this.currentY;
    const legendItemHeight = 4;
    let legendX = this.margin;
    const gap = 8;

    validCommunities.forEach((btn, index) => {
      this.pdf.setFillColor(btn.color);
      this.pdf.rect(legendX, legendY + 0.5, 2.5, 2.5, "F");

      this.pdf.setFontSize(5);
      this.pdf.setTextColor(80, 80, 80);
      const communityLabel =
        btn.text && btn.text.trim() !== "" ? btn.text : `C${index + 1}`;
      const labelText =
        communityLabel.length > 12
          ? communityLabel.substring(0, 12) + ".."
          : communityLabel;
      this.pdf.text(`${labelText}(${btn.type})`, legendX + 3, legendY + 3);

      legendX += this.pdf.getTextWidth(`${labelText}(${btn.type})`) + gap + 3;

      // 换行处理
      if (
        legendX > this.pageWidth - this.margin - 20 &&
        index < validCommunities.length - 1
      ) {
        legendX = this.margin;
        this.currentY += legendItemHeight + 2;
      }
    });

    this.currentY += legendItemHeight + 6;
  }

  // 添加国家和机构排名表
  addCountryAndInstitutionRankings(countryStats, institStats) {
    const cleanCountryCode = (text) => {
      if (!text) return "";
      if (typeof text !== "string") {
        text = String(text);
      }
      return text
        .replace(/[^\x00-\x7F]+/g, "")
        .replace(/[\u{1F600}-\u{1F64F}]/gu, "")
        .replace(/[\u{1F300}-\u{1F5FF}]/gu, "")
        .replace(/[\u{1F680}-\u{1F6FF}]/gu, "")
        .replace(/[\u{2600}-\u{26FF}]/gu, "")
        .replace(/[\u{2700}-\u{27BF}]/gu, "")
        .trim()
        .toUpperCase()
        .substring(0, 3);
    };

    const cleanText = (text) => {
      if (!text) return "";
      if (typeof text !== "string") {
        text = String(text);
      }
      return text
        .replace(/[^\x00-\x7F]+/g, "")
        .replace(/[\u{1F600}-\u{1F64F}]/gu, "")
        .replace(/[\u{1F300}-\u{1F5FF}]/gu, "")
        .replace(/[\u{1F680}-\u{1F6FF}]/gu, "")
        .replace(/[\u{2600}-\u{26FF}]/gu, "")
        .replace(/[\u{2700}-\u{27BF}]/gu, "");
    };

    if (
      (!countryStats || Object.keys(countryStats).length === 0) &&
      (!institStats || institStats.length === 0)
    )
      return;

    this.currentY += 2;

    if (
      countryStats &&
      Object.keys(countryStats).length > 0 &&
      institStats &&
      institStats.length > 0
    ) {
      this.addTitle("Country and Institution Rankings", 14);
      this.currentY += 3;

      const countryColWidth = (this.pageWidth - 3 * this.margin) * 0.25;
      const leftX = this.margin;
      const rightX = this.margin + countryColWidth;

      if (countryStats && Object.keys(countryStats).length > 0) {
        this.pdf.setFontSize(10);
        this.pdf.setFont("helvetica", "bold");
        this.pdf.setTextColor(this.textColor);
        this.pdf.text("Top Countries", leftX, this.currentY);

        const sortedCountries = Object.entries(countryStats)
          .sort(([, a], [, b]) => b - a)
          .slice(0, 12);

        let rowY = this.currentY + 4;
        const rowHeight = 6;

        this.pdf.setFontSize(7);
        this.pdf.setFont("helvetica", "bold");
        this.pdf.setDrawColor(180, 180, 180);
        this.pdf.setLineWidth(0.3);
        this.pdf.setFillColor(245, 245, 245);
        this.pdf.rect(leftX, rowY, countryColWidth - 10, rowHeight, "FD");
        this.pdf.text("Rank", leftX + 2, rowY + 4);
        this.pdf.text("Code", leftX + 12, rowY + 4);
        rowY += rowHeight;

        this.pdf.setDrawColor(200, 200, 200);
        this.pdf.setFont("helvetica", "normal");
        sortedCountries.forEach(([country, count], index) => {
          if (index < 12) {
            const cleanCountry = cleanCountryCode(String(country));
            if (index % 2 === 1) {
              this.pdf.setFillColor(250, 250, 250);
              this.pdf.rect(leftX, rowY, countryColWidth - 10, rowHeight, "FD");
            } else {
              this.pdf.rect(leftX, rowY, countryColWidth - 10, rowHeight, "D");
            }
            this.pdf.text(String(index + 1), leftX + 2, rowY + 4);
            this.pdf.text(cleanCountry, leftX + 12, rowY + 4);
            rowY += rowHeight;
          }
        });
      }

      if (institStats && institStats.length > 0) {
        this.pdf.setFontSize(10);
        this.pdf.setFont("helvetica", "bold");
        this.pdf.setTextColor(this.textColor);
        this.pdf.text("Top Institutions", rightX, this.currentY);

        const sortedInstitutes = institStats
          .sort((a, b) => b.count - a.count)
          .slice(0, 12);

        let rowY = this.currentY + 4;
        const rowHeight = 6;
        const rankColWidth = 12;
        const institColWidth = 70;
        const countryColWidth = 22;
        const papersColWidth = 12;

        this.pdf.setFontSize(7);
        this.pdf.setFont("helvetica", "bold");
        this.pdf.setDrawColor(180, 180, 180);
        this.pdf.setLineWidth(0.3);
        this.pdf.setFillColor(245, 245, 245);

        this.pdf.rect(rightX, rowY, rankColWidth, rowHeight, "FD");
        this.pdf.rect(
          rightX + rankColWidth,
          rowY,
          institColWidth,
          rowHeight,
          "FD"
        );
        this.pdf.rect(
          rightX + rankColWidth + institColWidth,
          rowY,
          countryColWidth,
          rowHeight,
          "FD"
        );
        this.pdf.rect(
          rightX + rankColWidth + institColWidth + countryColWidth,
          rowY,
          papersColWidth,
          rowHeight,
          "FD"
        );

        this.pdf.text("Rk", rightX + 2, rowY + 4);
        this.pdf.text("Institution", rightX + rankColWidth + 2, rowY + 4);
        this.pdf.text(
          "Code",
          rightX + rankColWidth + institColWidth + 2,
          rowY + 4
        );
        this.pdf.text(
          "Papers",
          rightX + rankColWidth + institColWidth + countryColWidth + 2,
          rowY + 4
        );
        rowY += rowHeight;

        this.pdf.setDrawColor(200, 200, 200);
        this.pdf.setFont("helvetica", "normal");
        this.pdf.setFontSize(6);
        sortedInstitutes.forEach((instit, index) => {
          if (index < 12) {
            const cleanInstitName = cleanText(String(instit.instit)).substring(
              0,
              45
            );
            const cleanCountry = cleanCountryCode(String(instit.country));

            if (index % 2 === 1) {
              this.pdf.setFillColor(250, 250, 250);
              this.pdf.rect(rightX, rowY, rankColWidth, rowHeight, "FD");
              this.pdf.rect(
                rightX + rankColWidth,
                rowY,
                institColWidth,
                rowHeight,
                "FD"
              );
              this.pdf.rect(
                rightX + rankColWidth + institColWidth,
                rowY,
                countryColWidth,
                rowHeight,
                "FD"
              );
              this.pdf.rect(
                rightX + rankColWidth + institColWidth + countryColWidth,
                rowY,
                papersColWidth,
                rowHeight,
                "FD"
              );
            } else {
              this.pdf.rect(rightX, rowY, rankColWidth, rowHeight, "D");
              this.pdf.rect(
                rightX + rankColWidth,
                rowY,
                institColWidth,
                rowHeight,
                "D"
              );
              this.pdf.rect(
                rightX + rankColWidth + institColWidth,
                rowY,
                countryColWidth,
                rowHeight,
                "D"
              );
              this.pdf.rect(
                rightX + rankColWidth + institColWidth + countryColWidth,
                rowY,
                papersColWidth,
                rowHeight,
                "D"
              );
            }

            this.pdf.text(String(index + 1), rightX + 2, rowY + 4);
            this.pdf.text(cleanInstitName, rightX + rankColWidth + 2, rowY + 4);
            this.pdf.text(
              cleanCountry,
              rightX + rankColWidth + institColWidth + 2,
              rowY + 4
            );
            this.pdf.text(
              String(instit.count),
              rightX + rankColWidth + institColWidth + countryColWidth + 2,
              rowY + 4
            );
            rowY += rowHeight;
          }
        });
      }

      this.currentY +=
        Math.max(
          countryStats && Object.keys(countryStats).length > 0 ? 6 * 12 : 0,
          institStats && institStats.length > 0 ? 6 * 12 : 0
        ) + 8;
    } else if (countryStats && Object.keys(countryStats).length > 0) {
      this.addSubTitle("Country Rankings", 12);
      this.currentY += 2;

      const sortedCountries = Object.entries(countryStats)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 12);

      const countryData = sortedCountries.map(([country, count]) => [
        String(sortedCountries.findIndex(([c]) => c === country) + 1),
        cleanCountryCode(String(country)),
      ]);

      this.addTable(countryData, ["Rank", "Code"], 10);
      this.currentY += 5;
    } else if (institStats && institStats.length > 0) {
      this.addSubTitle("Institution Rankings", 12);
      this.currentY += 2;

      const sortedInstitutes = institStats
        .sort((a, b) => b.count - a.count)
        .slice(0, 12);

      const institData = sortedInstitutes.map((instit, index) => [
        String(index + 1),
        cleanText(String(instit.instit)).substring(0, 40),
        cleanCountryCode(String(instit.country)),
        instit.count,
      ]);

      this.addTable(institData, ["Rank", "Institution", "Code", "Papers"], 10);
      this.currentY += 5;
    }
  }

  // 添加图像
  async addImageFromElement(element, maxWidth = 160, maxHeight = 100) {
    try {
      // 检查元素是否存在
      if (!element) {
        console.warn("Element not found for capture");
        return;
      }

      // 检查元素尺寸
      const elemWidth = element.offsetWidth || element.scrollWidth;
      const elemHeight = element.offsetHeight || element.scrollHeight;

      if (elemWidth === 0 || elemHeight === 0) {
        console.warn("Element has zero dimensions:", elemWidth, elemHeight);
        return;
      }

      console.log("Capturing element with dimensions:", elemWidth, elemHeight);

      // 根据元素比例计算实际显示尺寸
      const aspectRatio = elemWidth / elemHeight;
      let displayWidth = maxWidth;
      let displayHeight = maxWidth / aspectRatio;

      // 如果高度超过最大高度，则按高度缩放
      if (displayHeight > maxHeight) {
        displayHeight = maxHeight;
        displayWidth = maxHeight * aspectRatio;
      }

      const canvas = await html2canvas(element, {
        scale: 2, // 提高分辨率
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        width: elemWidth,
        height: elemHeight,
        scrollX: 0,
        scrollY: 0,
      });

      const imgData = canvas.toDataURL("image/png", 1.0); // 最高质量
      this.checkPageBreak(displayHeight + 10);
      this.pdf.addImage(
        imgData,
        "PNG",
        this.margin,
        this.currentY,
        displayWidth,
        displayHeight
      );
      this.currentY += displayHeight + 10;
    } catch (error) {
      console.warn("Failed to capture image:", error);
    }
  }

  // 添加树状图文本内容
  addTreeData(treeData) {
    if (!treeData) {
      return;
    }

    // 递归渲染树结构
    const renderNode = (node, level = 0, index = 1) => {
      const indent = "  ".repeat(level);

      // 获取节点名称
      const nodeName = node.info?.name || node.name || `Concept ${node.id}`;

      // 添加节点名称
      this.setTextStyle(10 + Math.max(0, 2 - level), this.primaryColor, "bold");
      this.checkPageBreak(12);

      this.pdf.text(`${indent}${nodeName}`, this.margin, this.currentY);
      this.currentY += 6;

      // 添加节点详细信息
      if (node.info) {
        this.setTextStyle(9, this.secondaryColor, "normal");
        const indentInfo = "  ".repeat(level + 1);
        let infoText = "";

        // ID信息
        infoText += `ID: ${node.info.id || node.id}`;

        // 度数/论文数量
        if (node.info.degree !== undefined) {
          infoText += ` | Degree: ${node.info.degree}`;
        }

        if (infoText) {
          this.pdf.text(
            `${indentInfo}${infoText}`,
            this.margin + 10,
            this.currentY
          );
          this.currentY += 5;
        }
      }

      // 递归渲染子节点
      if (node.children && node.children.length > 0) {
        node.children.forEach((child, childIndex) => {
          renderNode(child, level + 1, childIndex + 1);
        });
      }

      this.currentY += 2; // 节点间距
    };

    // 添加树标题信息
    const rootName = treeData.info?.name || treeData.name || `Tree Structure`;
    this.setTextStyle(12, this.primaryColor, "bold");
    this.addText(`Concept Flow Tree: ${rootName}`, 12);
    this.currentY += 4;

    // 渲染根节点及其所有子节点
    renderNode(treeData, 0);

    // 添加统计信息
    const countNodes = (node) => {
      let count = 1;
      if (node.children) {
        count += node.children.reduce(
          (sum, child) => sum + countNodes(child),
          0
        );
      }
      return count;
    };

    const totalNodes = countNodes(treeData);
    this.setTextStyle(8, this.tertiaryColor, "italic");
    this.addText(`Total concepts: ${totalNodes}`, 8);
    this.currentY += 6;
  }

  // 添加思维导图文本内容
  addMindmapContent(mindmapContent) {
    if (!mindmapContent || mindmapContent.trim() === "") {
      return;
    }

    this.checkPageBreak(200);
    this.currentY += 8;

    // 文本清理函数（移除特殊字符和emoji）
    const cleanText = (text) => {
      if (!text) return "";

      // 确保text是字符串类型
      if (typeof text !== "string") {
        text = String(text);
      }

      return text
        .replace(/[^\x00-\x7F]+/g, "")
        .replace(/[\u{1F600}-\u{1F64F}]/gu, "")
        .replace(/[\u{1F300}-\u{1F5FF}]/gu, "")
        .replace(/[\u{1F680}-\u{1F6FF}]/gu, "")
        .replace(/[\u{2600}-\u{26FF}]/gu, "")
        .replace(/[\u{2700}-\u{27BF}]/gu, "");
    };

    // 解析markdown内容并格式化显示
    const lines = mindmapContent.split("\n");
    let currentIndent = 0;

    lines.forEach((line) => {
      // 保留原始空格来检测嵌套层级
      const originalLine = line;
      const trimmedLine = line.trim();

      if (!trimmedLine) {
        this.currentY += 3; // 空行间距
        return;
      }

      // 检测标题级别
      if (trimmedLine.startsWith("#")) {
        const level = trimmedLine.match(/^#+/)[0].length;
        const titleText = cleanText(trimmedLine.replace(/^#+\s*/, ""));

        // 增强的层级缩进 - 更大更明显的缩进差异
        currentIndent = Math.max(0, level - 1) * 25;

        // 添加层级缩进线和连接线，增强视觉效果
        if (level > 1) {
          this.pdf.setDrawColor("#cccccc");
          this.pdf.setLineWidth(0.5);
          const lineY = this.currentY + 8;
          this.pdf.line(
            this.margin + currentIndent - 20,
            lineY,
            this.margin + currentIndent - 5,
            lineY
          );
        }

        // 设置标题样式 - 不同级别有明显的视觉差异，并处理文本换行
        if (level === 1) {
          this.setTextStyle(16, this.primaryColor, "bold");
          this.currentY += 8;
          // 计算可用宽度并处理换行
          const maxWidth =
            this.pageWidth - 2 * this.margin - currentIndent - 10;
          const titleLines = this.pdf.splitTextToSize(titleText, maxWidth);
          titleLines.forEach((line, index) => {
            this.pdf.text(
              line,
              this.margin + currentIndent,
              this.currentY + index * 8
            );
          });
          this.currentY += titleLines.length * 8;
        } else if (level === 2) {
          this.setTextStyle(14, this.primaryColor, "bold");
          this.currentY += 6;
          // 计算可用宽度并处理换行
          const maxWidth =
            this.pageWidth - 2 * this.margin - currentIndent - 10;
          const titleLines = this.pdf.splitTextToSize(titleText, maxWidth);
          titleLines.forEach((line, index) => {
            this.pdf.text(
              line,
              this.margin + currentIndent,
              this.currentY + index * 7
            );
          });
          this.currentY += titleLines.length * 7;
        } else if (level === 3) {
          this.setTextStyle(12, this.primaryColor, "bold");
          this.currentY += 4;
          // 计算可用宽度并处理换行
          const maxWidth =
            this.pageWidth - 2 * this.margin - currentIndent - 10;
          const titleLines = this.pdf.splitTextToSize(titleText, maxWidth);
          titleLines.forEach((line, index) => {
            this.pdf.text(
              line,
              this.margin + currentIndent,
              this.currentY + index * 6
            );
          });
          this.currentY += titleLines.length * 6;
        } else {
          this.setTextStyle(11, this.primaryColor, "bold");
          this.currentY += 3;
          // 计算可用宽度并处理换行
          const maxWidth =
            this.pageWidth - 2 * this.margin - currentIndent - 10;
          const titleLines = this.pdf.splitTextToSize(titleText, maxWidth);
          titleLines.forEach((line, index) => {
            this.pdf.text(
              line,
              this.margin + currentIndent,
              this.currentY + index * 5
            );
          });
          this.currentY += titleLines.length * 5;
        }

        // 不同级别标题的底部间距
        if (level === 1) {
          this.currentY += 8;
        } else if (level === 2) {
          this.currentY += 6;
        } else {
          this.currentY += 4;
        }
      }
      // 检测列表项（包括嵌套列表）
      else if (
        trimmedLine.startsWith("-") ||
        trimmedLine.startsWith("*") ||
        trimmedLine.startsWith("•")
      ) {
        const listText = cleanText(trimmedLine.replace(/^[-*•]\s*/, ""));

        // 计算嵌套层级 - 基于空格数量
        const leadingSpaces =
          originalLine.length - originalLine.trimStart().length;
        const listLevel = Math.floor(leadingSpaces / 2); // 每2个空格为一级缩进
        currentIndent = listLevel * 20; // 减少缩进距离

        // 检测加粗文本
        const boldText = listText.match(/\*\*(.*?)\*\*/);
        let displayText = listText;

        if (boldText) {
          displayText = listText.replace(/\*\*(.*?)\*\*/g, "$1");
        }

        this.checkPageBreak(12); // 减少页面检查高度

        // 根据层级设置不同颜色，不使用特殊符号
        let textColor = this.textColor;
        let fontSize = 10;

        if (listLevel === 0) {
          textColor = this.primaryColor; // 主色
          fontSize = 11;
        } else if (listLevel === 1) {
          textColor = "#0066cc"; // 蓝色
          fontSize = 10;
        } else if (listLevel === 2) {
          textColor = "#008844"; // 绿色
          fontSize = 9;
        } else {
          textColor = "#666666"; // 灰色
          fontSize = 9;
        }

        this.setTextStyle(fontSize, textColor, boldText ? "bold" : "normal");

        // 显示缩进指示符"-"和文本，并处理文本换行
        this.pdf.setTextColor(textColor);

        // 计算可用宽度
        const maxWidth = this.pageWidth - 2 * this.margin - currentIndent - 20;
        const textLines = this.pdf.splitTextToSize(displayText, maxWidth);

        // 为每个缩进级别添加"-"符号，只在第一行显示
        let indentText = "";
        for (let i = 0; i < listLevel; i++) {
          indentText += "-";
        }

        if (indentText) {
          this.pdf.text(
            indentText,
            this.margin + currentIndent - listLevel * 8,
            this.currentY
          );
        }

        // 绘制文本行，处理换行
        textLines.forEach((line, lineIndex) => {
          const xPos =
            lineIndex === 0
              ? this.margin + currentIndent
              : this.margin + currentIndent + listLevel * 8;
          this.pdf.text(line, xPos, this.currentY + lineIndex * 4);
        });

        this.currentY += textLines.length * 4; // 根据行数调整间距
      }
      // 普通段落，处理换行
      else {
        this.checkPageBreak(12);

        // 处理加粗文本，并计算换行
        const boldParts = cleanText(trimmedLine).split(/(\*\*.*?\*\*)/g);

        // 计算可用宽度
        const maxWidth = this.pageWidth - 2 * this.margin - currentIndent - 10;

        // 合并所有文本到一个字符串来计算换行
        let fullText = "";
        boldParts.forEach((part) => {
          if (part.startsWith("**") && part.endsWith("**")) {
            fullText += part.slice(2, -2);
          } else if (part) {
            fullText += part;
          }
        });

        // 按行分割文本
        const textLines = this.pdf.splitTextToSize(fullText, maxWidth);

        // 绘制文本行
        textLines.forEach((line, lineIndex) => {
          let xPosition = this.margin + currentIndent;
          let currentLine = line;

          // 处理这一行的加粗文本
          const lineBoldParts = line.split(/(\*\*.*?\*\*)/g);

          lineBoldParts.forEach((part) => {
            if (part.startsWith("**") && part.endsWith("**")) {
              // 加粗文本
              const text = part.slice(2, -2);
              if (currentLine) {
                // 先绘制当前普通文本
                this.setTextStyle(10, this.textColor, "normal");
                const normalWidth = this.pdf.getTextWidth(currentLine);
                this.pdf.text(
                  currentLine,
                  xPosition,
                  this.currentY + lineIndex * 4
                );
                xPosition += normalWidth;
                currentLine = "";
              }
              // 绘制加粗文本
              this.setTextStyle(10, this.textColor, "bold");
              const boldWidth = this.pdf.getTextWidth(text);
              this.pdf.text(text, xPosition, this.currentY + lineIndex * 4);
              xPosition += boldWidth;
            } else if (part) {
              // 普通文本
              this.setTextStyle(10, this.textColor, "normal");
              currentLine += part;
            }
          });

          // 绘制剩余的普通文本
          if (currentLine) {
            this.pdf.text(
              currentLine,
              xPosition,
              this.currentY + lineIndex * 4
            );
          }
        });

        this.currentY += textLines.length * 4;
      }
    });

    this.currentY += 12;
  }

  // 添加页面底部信息（ICLR 风格）
  addFooter(pageNumber = null) {
    const totalPages = this.pdf.internal.getNumberOfPages();
    const currentPage = pageNumber || totalPages;

    this.pdf.setFontSize(8);
    this.pdf.setFont("helvetica", "normal");
    this.pdf.setTextColor(150, 150, 150);

    // 左侧：网站信息
    this.pdf.text(
      "DeepReport | idea.acemap.cn",
      this.margin,
      this.pageHeight - 10
    );

    // 右侧：页码信息
    this.pdf.text(
      `Page ${currentPage} of ${totalPages}`,
      this.pageWidth - this.margin - 25,
      this.pageHeight - 10
    );
  }

  // 主要的PDF生成方法
  async generateReport(data) {
    const {
      keywords,
      topics_ls = [],
      new_relation_table = [],
      infoList = [],
      abstract_text = "",
      conclusion_text = "",
      year_freq = [],
      graph_data = null,
      country_stats = {},
      instit_stats_ls = [],
      btnList = [],
      graphElement = null,
      treeData = data.treeData || null,
      mindmapContent = data.mindmapContent || null,
      topic_gpt_detail = {},
    } = data;

    // 清理UTF-8字符和emoji的辅助函数
    const cleanText = (text) => {
      if (!text) return "";

      // 确保text是字符串类型
      if (typeof text !== "string") {
        text = String(text);
      }

      return text
        .replace(/[^\x00-\x7F]+/g, "")
        .replace(/[\u{1F600}-\u{1F64F}]/gu, "")
        .replace(/[\u{1F300}-\u{1F5FF}]/gu, "")
        .replace(/[\u{1F680}-\u{1F6FF}]/gu, "")
        .replace(/[\u{2600}-\u{26FF}]/gu, "")
        .replace(/[\u{2700}-\u{27BF}]/gu, "");
    };

    // 封面页 - ICLR 论文风格
    await this.addLogoToFirstPage();

    // 论文标题
    this.currentY = this.margin + 40;
    const paperTitleText = `Research Report: ${cleanText(String(keywords))}`;
    this.addPaperTitle(paperTitleText);

    // 作者信息
    this.addAuthors(["DeepReport System"]);
    this.currentY += 3;

    // 生成日期和链接
    this.setTextStyle(9, this.tertiaryColor, "normal");
    const dateText = `Generated on ${new Date().toLocaleDateString()}`;
    const dateWidth = this.pdf.getTextWidth(dateText);
    this.pdf.text(dateText, (this.pageWidth - dateWidth) / 2, this.currentY);
    this.currentY += 8;

    // 摘要 - 优先使用 AI 第一次回复
    const abstractContent = abstract_text || conclusion_text;
    if (abstractContent) {
      this.addAbstract(cleanText(String(abstractContent)));
    }

    // 分隔线
    this.addSeparator();

    // Research Overview - 改进为卡片式布局
    this.addSubTitle("1 Research Overview", 12);
    this.currentY += 3;

    // 统计数据卡片
    const stats = [
      { label: "Papers", value: infoList.length || 0, icon: "" },
      { label: "Concepts", value: graph_data?.nodes?.length || 0, icon: "" },
      { label: "Connections", value: graph_data?.edges?.length || 0, icon: "" },
      {
        label: "Countries",
        value: Object.keys(country_stats).length,
        icon: "",
      },
    ];

    // 绘制统计卡片
    const cardWidth = (this.pageWidth - 2 * this.margin - 12) / 4;
    const cardHeight = 18;
    const cardY = this.currentY;
    const cardGap = 4;

    stats.forEach((stat, index) => {
      const cardX = this.margin + index * (cardWidth + cardGap);

      // 卡片背景
      this.pdf.setFillColor(248, 249, 250);
      this.pdf.setDrawColor(222, 226, 230);
      this.pdf.setLineWidth(0.3);
      this.pdf.roundedRect(cardX, cardY, cardWidth, cardHeight, 2, 2, "FD");

      // 数值
      this.pdf.setFontSize(14);
      this.pdf.setFont("helvetica", "bold");
      this.pdf.setTextColor(33, 37, 41);
      const valueStr = String(stat.value);
      const valueWidth = this.pdf.getTextWidth(valueStr);
      this.pdf.text(valueStr, cardX + (cardWidth - valueWidth) / 2, cardY + 8);

      // 标签
      this.pdf.setFontSize(7);
      this.pdf.setFont("helvetica", "normal");
      this.pdf.setTextColor(108, 117, 125);
      const labelWidth = this.pdf.getTextWidth(stat.label);
      this.pdf.text(
        stat.label,
        cardX + (cardWidth - labelWidth) / 2,
        cardY + 14
      );
    });

    this.currentY += cardHeight + 8;

    // 时间跨度信息
    if (year_freq.length > 0) {
      const minYear = Math.min(...year_freq.map((y) => y.year));
      const maxYear = Math.max(...year_freq.map((y) => y.year));
      this.setTextStyle(8, this.tertiaryColor, "normal");
      this.pdf.text(
        `Research Period: ${minYear} - ${maxYear}`,
        this.margin,
        this.currentY
      );
      this.currentY += 5;
    }

    // 未来研究方向数量
    if (topics_ls.length > 0) {
      this.setTextStyle(8, this.tertiaryColor, "normal");
      this.pdf.text(
        `Research Directions Identified: ${topics_ls.length}`,
        this.margin,
        this.currentY
      );
      this.currentY += 5;
    }

    // 新发现关系数量
    if (new_relation_table.length > 0) {
      this.setTextStyle(8, this.tertiaryColor, "normal");
      this.pdf.text(
        `Novel Connections: ${new_relation_table.length}`,
        this.margin,
        this.currentY
      );
      this.currentY += 8;
    }

    // 未来研究方向
    if (topics_ls.length > 0) {
      this.checkPageBreak(60);
      this.addSubTitle("2 Future Research Directions", 12);
      this.currentY += 2;

      topics_ls.forEach((topic, index) => {
        this.checkPageBreak(10);
        this.addText(`${index + 1}. ${cleanText(String(topic))}`, 9);
        this.currentY += 2;
      });
      this.currentY += 5;
    }

    // Top 概念图表
    if (graph_data && graph_data.nodes) {
      try {
        this.checkPageBreak(80);
        this.addSubTitle("3 Top Concepts Analysis", 12);
        this.currentY += 3;
        this.addTopConceptsChart(graph_data);
      } catch (error) {
        console.warn("Failed to add Top Concepts Chart:", error);
      }
    }

    // 概念社区图表
    if (btnList.length > 0) {
      try {
        this.checkPageBreak(60);
        this.addSubTitle("4 Concept Communities", 12);
        this.currentY += 3;
        this.addCommunityChart(btnList);
      } catch (error) {
        console.warn("Failed to add Community Chart:", error);
      }
    }

    // 国家和机构排名表
    if (Object.keys(country_stats).length > 0 || instit_stats_ls.length > 0) {
      try {
        this.checkPageBreak(80);
        this.addSubTitle("5 Country and Institution Rankings", 12);
        this.currentY += 3;
        this.addCountryAndInstitutionRankings(country_stats, instit_stats_ls);
      } catch (error) {
        console.warn("Failed to add Country and Institution Rankings:", error);
      }
    }

    // 概念图和新连接关系表格
    if (graphElement || new_relation_table.length > 0) {
      this.checkPageBreak(150);
      this.currentY += 5;

      if (graphElement) {
        this.addSubTitle("6 Concept Co-occurrence Graph", 12);
        this.currentY += 3;
        try {
          await this.addImageFromElement(
            graphElement,
            this.pageWidth - 2 * this.margin,
            250
          );
        } catch (error) {
          console.warn(
            "Failed to add Concept Co-occurrence Graph image:",
            error
          );
        }
        this.currentY += 8;
      }

      if (new_relation_table.length > 0) {
        try {
          this.addSubTitle("7 Novel Connections Discovered", 12);
          this.currentY += 3;

          const tableData = new_relation_table
            .slice(0, 10)
            .map((row) => [
              row.source_name || "N/A",
              row.target_name || "N/A",
              row.weight ? row.weight.toFixed(3) : "N/A",
              row.year || "N/A",
            ]);

          this.addTable(
            tableData,
            ["Source Concept", "Target Concept", "Weight", "Year"],
            10
          );
        } catch (error) {
          console.warn("Failed to add Novel Connections table:", error);
        }
      }
    }

    // 概念流图（Tree）- 暂时禁用
    if (false && treeData) {
      this.checkPageBreak(150);
      this.addSubTitle("Concept Flow Tree", 12);
      this.currentY += 3;
      this.addTreeData(treeData);
      this.currentY += 8;
    }

    // 论文列表 - ICLR 参考文献 style
    try {
      if (infoList && infoList.length > 0) {
        this.checkPageBreak(100);
        this.addSubTitle("8 References", 12);
        this.currentY += 3;

        const sortedInfoList = [...infoList].sort((a, b) => {
          const citationsA = a.citations || 0;
          const citationsB = b.citations || 0;
          return citationsB - citationsA;
        });

        sortedInfoList.forEach((paper, index) => {
          this.checkPageBreak(14);

          if (index > 0) {
            this.currentY += 5;
          }

          const isHighCitation = paper.high_cite || paper.citations > 100;
          const citationText = paper.citations
            ? `(${paper.citations} citations)`
            : "";
          const titleText = cleanText(String(paper.title || "No Title"));
          const yearText = paper.year ? `[${paper.year}]` : "[Unknown]";

          if (isHighCitation) {
            this.setTextStyle(9, this.textColor, "bold");
          } else {
            this.setTextStyle(9, this.textColor, "normal");
          }

          const maxWidth = this.pageWidth - 2 * this.margin;
          const formattedYear = yearText
            .replace(/\[(\d{4})\]/, "$1")
            .replace("Unknown", "n.d.");

          this.pdf.text(`[${index + 1}]`, this.margin, this.currentY);

          const titleLines = this.pdf.splitTextToSize(titleText, maxWidth - 12);

          if (paper.url && paper.url.trim() !== "") {
            this.pdf.setTextColor(this.linkColor);
            titleLines.forEach((line, lineIndex) => {
              const lineY = this.currentY + lineIndex * 4;
              this.pdf.text(line, this.margin + 12, lineY);
              const lineWidth = this.pdf.getTextWidth(line);
              this.pdf.link(this.margin + 12, lineY - 3, lineWidth, 4, {
                url: paper.url.trim(),
              });
            });
            this.pdf.setTextColor(this.textColor);
          } else {
            this.pdf.text(titleLines, this.margin + 12, this.currentY);
          }

          this.currentY += titleLines.length * 4;

          this.setTextStyle(7, this.tertiaryColor, "normal");
          this.pdf.text(formattedYear, this.margin + 12, this.currentY);
          this.currentY += 3;

          let metaText = "";
          let authorDisplay = "";

          if (paper.author && paper.author.length > 0) {
            if (Array.isArray(paper.author)) {
              authorDisplay =
                paper.author.length > 2
                  ? paper.author.slice(0, 2).join(", ") + ", et al."
                  : paper.author.join(", ");
            } else {
              authorDisplay = String(paper.author);
            }
          } else if (paper.authors) {
            if (typeof paper.authors === "string") {
              authorDisplay = paper.authors;
            } else {
              authorDisplay = String(paper.authors);
            }
          } else {
            authorDisplay = "Unknown";
          }

          authorDisplay = String(authorDisplay);
          metaText += cleanText(String(authorDisplay));

          if (paper.journal) {
            metaText += `. ${cleanText(String(paper.journal))}`;
          }

          if (citationText) {
            metaText += `. Citations: ${paper.citations}`;
          }

          this.setTextStyle(7, this.tertiaryColor, "normal");
          const metaLines = this.pdf.splitTextToSize(
            metaText,
            this.pageWidth - 2 * this.margin - 12
          );
          this.pdf.text(metaLines, this.margin + 12, this.currentY);
          this.currentY += metaLines.length * 3;

          if (paper.field && paper.field.length > 0) {
            this.setTextStyle(7, this.tertiaryColor, "italic");
            let fields = "";

            if (Array.isArray(paper.field)) {
              fields = String(paper.field.slice(0, 2).join(", "));
            } else {
              fields = String(paper.field);
            }

            const fieldsText = `Fields: ${cleanText(String(fields))}`;
            const fieldsLines = this.pdf.splitTextToSize(
              fieldsText,
              this.pageWidth - 2 * this.margin - 12
            );
            this.pdf.text(fieldsLines, this.margin + 12, this.currentY);
            this.currentY += fieldsLines.length * 3;
          } else {
            this.currentY += 2;
          }
        });
      }
    } catch (error) {
      console.warn("Failed to add References section:", error);
    }

    // 知识思维导图（Mindmap）
    try {
      if (mindmapContent) {
        this.checkPageBreak(200);
        this.addSubTitle("9 Knowledge Mindmap", 12);
        this.currentY += 3;
        this.addMindmapContent(mindmapContent);
      }
    } catch (error) {
      console.warn("Failed to add Knowledge Mindmap section:", error);
    }

    // 附录：Future Research Directions - Detailed Insights
    try {
      if (topics_ls && topics_ls.length > 0) {
        this.pdf.addPage();
        this.currentY = this.margin + 10;
        this.addSubTitle("Appendix A: Future Research Directions", 12);
        this.currentY += 3;

        topics_ls.forEach((topic, index) => {
          const topicKey = String(topic);
          const detailText =
            topic_gpt_detail && topic_gpt_detail[topicKey]
              ? topic_gpt_detail[topicKey]
              : "";

          let estimatedHeight = 30;
          if (detailText && detailText.trim()) {
            const maxWidth = this.pageWidth - 2 * this.margin - 10;
            const detailLines = this.pdf.splitTextToSize(
              cleanText(String(detailText)),
              maxWidth
            );
            estimatedHeight += detailLines.length * 3 + 5;
          }
          this.checkPageBreak(estimatedHeight);

          this.setTextStyle(10, this.textColor, "bold");
          this.addText(`${index + 1}. ${cleanText(topicKey)}`, 9);
          this.currentY += 2;

          if (detailText && detailText.trim()) {
            this.setTextStyle(8, this.tertiaryColor, "italic");
            this.addText("Related Research Context:", 8);
            this.currentY += 1;

            this.setTextStyle(8, this.textColor, "normal");
            const maxWidth = this.pageWidth - 2 * this.margin - 5;

            const detailLines = this.pdf.splitTextToSize(
              cleanText(String(detailText)),
              maxWidth
            );

            detailLines.forEach((line) => {
              const textWidth = this.pdf.getTextWidth(line);
              const xPosition = Math.min(
                this.margin + 5,
                this.pageWidth - this.margin - textWidth
              );
              this.pdf.text(line, xPosition, this.currentY);
              this.currentY += 3;
            });

            this.currentY += 3;
          } else {
            this.setTextStyle(8, this.tertiaryColor, "italic");
            this.addText(
              "No detailed insights available for this research direction.",
              8
            );
            this.currentY += 3;
          }
        });
      }
    } catch (error) {
      console.warn("Failed to add Future Research Directions section:", error);
    }

    // 附录：New Connections Knowledge Text
    try {
      if (new_relation_table && new_relation_table.length > 0) {
        const validConnections = new_relation_table.filter((connection) => {
          const connectionText = connection.knowledge_text || "";
          return (
            connectionText &&
            connectionText.trim() !== "" &&
            connectionText !==
              "No knowledge text available for this connection."
          );
        });

        if (validConnections.length > 0) {
          this.pdf.addPage();
          this.currentY = this.margin + 10;
          this.addSubTitle("Appendix B: Novel Connections Analysis", 12);
          this.currentY += 3;

          validConnections.forEach((connection, displayIndex) => {
            const sourceName = (
              connection.source_name || "Unknown Source"
            ).replace(/[^\w\s-]/g, "");
            const targetName = (
              connection.target_name || "Unknown Target"
            ).replace(/[^\w\s-]/g, "");
            let connectionText = connection.knowledge_text || "";

            connectionText = connectionText.replace(/<br\s*\/?>/gi, "");

            const maxWidth = this.pageWidth - 2 * this.margin - 5;
            const textLines = this.pdf.splitTextToSize(
              cleanText(String(connectionText)),
              maxWidth
            );
            const estimatedHeight = 35 + textLines.length * 3;
            this.checkPageBreak(estimatedHeight);

            this.setTextStyle(10, this.textColor, "bold");
            this.addText(
              `Connection ${displayIndex + 1}: ${cleanText(
                sourceName
              )} & ${cleanText(targetName)}`,
              9
            );
            this.currentY += 2;

            if (connection.weight || connection.year) {
              this.setTextStyle(8, this.tertiaryColor, "italic");
              let metaInfo = [];
              if (connection.weight)
                metaInfo.push(`Weight: ${connection.weight.toFixed(3)}`);
              if (connection.year) metaInfo.push(`Year: ${connection.year}`);
              this.addText(metaInfo.join(" | "), 8);
              this.currentY += 1;
            }

            this.setTextStyle(8, this.tertiaryColor, "italic");
            this.addText("Conceptual Connection Analysis:", 8);
            this.currentY += 1;

            this.setTextStyle(8, this.textColor, "normal");

            const textMaxWidth = this.pageWidth - 2 * this.margin - 5;
            const reCalcLines = this.pdf.splitTextToSize(
              cleanText(String(connectionText)),
              textMaxWidth
            );

            reCalcLines.forEach((line) => {
              const textWidth = this.pdf.getTextWidth(line);
              const xPosition = Math.min(
                this.margin + 5,
                this.pageWidth - this.margin - textWidth
              );
              this.pdf.text(line, xPosition, this.currentY);
              this.currentY += 3;
            });

            this.currentY += 4;
          });
        }
      }
    } catch (error) {
      console.warn(
        "Failed to add New Connections Knowledge Text section:",
        error
      );
    }

    // 添加DeepReport介绍部分 - 新页面
    try {
      // 先分页，确保另起一页
      this.pdf.addPage();
      this.currentY = this.margin + 10;

      // 添加技术论文介绍
      this.setTextStyle(12, this.primaryColor, "bold");
      this.addText("DeepReport Technical Paper", 12);
      this.currentY += 4;

      this.setTextStyle(10, this.textColor, "normal");
      const introText = `This is the official website for the paper "Exploring and Verbalizing Academic Ideas by Concept Co-occurrence" by Yi Xu, Shuqian Sheng, Bo Xue, Luoyi Fu, Xinbing Wang, and Chenghu Zhou. The paper proposes a framework based on concept co-occurrence for academic inspiration, which has been integrated into a research assistant system DeepReport. The system can assist researchers in expediting the process of discovering new ideas.`;

      const introLines = this.pdf.splitTextToSize(
        cleanText(introText),
        this.pageWidth - 2 * this.margin
      );
      introLines.forEach((line) => {
        this.pdf.text(line, this.margin, this.currentY);
        this.currentY += 4;
      });

      this.currentY += 6;

      // 添加GitHub链接信息
      this.setTextStyle(10, this.secondaryColor, "italic");
      this.addText("Official Repository:", 10);
      this.currentY += 2;

      this.setTextStyle(9, this.textColor, "normal");
      this.addText("https://github.com/xyjigsaw/Kiscovery", 9);
      this.currentY += 8;

      // 添加概念共现图信息
      this.setTextStyle(12, this.primaryColor, "bold");
      this.addText("Evolving Concept Co-occurrence Graph", 12);
      this.currentY += 4;

      this.setTextStyle(10, this.textColor, "normal");
      const graphText = `To train our model for temporal link prediction, we first collect 240 essential and common queries from 19 disciplines and one special topic (COVID-19). Then, we enter these queries into the paper database to fetch the most relevant papers between 2000 and 2021 with Elasticsearch, a modern text retrieval engine that stores and retrieves papers. Afterward, we use information extraction tools including AutoPhrase to identify concepts. Only high-quality concepts that appear in our database will be preserved. Finally, we construct 240 evolving concept co-occurrence graphs, each containing 22 snapshots according to the co-occurrence relationship. The statistics of the concept co-occurrence graphs are provided in Appendix I.`;

      const graphLines = this.pdf.splitTextToSize(
        cleanText(graphText),
        this.pageWidth - 2 * this.margin
      );
      graphLines.forEach((line) => {
        this.pdf.text(line, this.margin, this.currentY);
        this.currentY += 4;
      });

      this.currentY += 4;

      this.setTextStyle(9, this.secondaryColor, "italic");
      this.addText(
        "Download from Huggingface: https://huggingface.co/datasets/Reacubeth/ConceptGraph",
        9
      );
      this.currentY += 6;

      // 添加下载命令
      this.setTextStyle(9, this.secondaryColor, "italic");
      this.addText("Download with git (install git-lfs first):", 9);
      this.currentY += 2;

      this.setTextStyle(8, this.textColor, "normal");
      this.addText("git lfs install", 8);
      this.currentY += 2;
      this.addText(
        "git clone https://huggingface.co/datasets/Reacubeth/ConceptGraph",
        8
      );
      this.currentY += 6;

      // 检查是否需要分页
      this.checkPageBreak(80);

      // 添加共现引用五元组信息
      this.setTextStyle(12, this.primaryColor, "bold");
      this.addText("Co-occurrence Citation Quintuple", 12);
      this.currentY += 4;

      this.setTextStyle(10, this.textColor, "normal");
      const quintupleText = `We construct and release a dataset of co-occurrence citation quintuples, which is used to train text generation model for idea verbalization. The process of identifying and processing concepts is similar to constructing the concept co-occurrence graph. Heuristic rules are adopted to filter redundant and noisy sentences, further improving the quality of the quintuples used for idea generation. More details of co-occurrence citation quintuples can be found in Appendix B, C, and J.`;

      const quintupleLines = this.pdf.splitTextToSize(
        cleanText(quintupleText),
        this.pageWidth - 2 * this.margin
      );
      quintupleLines.forEach((line) => {
        this.pdf.text(line, this.margin, this.currentY);
        this.currentY += 4;
      });

      this.currentY += 4;

      this.setTextStyle(9, this.secondaryColor, "italic");
      this.addText(
        "In mid-2023, our DeepReport system underwent a major update, encompassing both data and model improvements. On the data front, we introduced a new version of the quintuple data (V202306), resulting in enhanced quality and a larger-scale dataset.",
        9
      );
      this.currentY += 6;

      // 添加数据集统计表格说明
      this.setTextStyle(10, this.textColor, "normal");
      const tableText = `The statistical summary of the new quintuple data (V202306) is presented as follows. The total dataset contains over 5.3 million quintuples across 19 disciplines, with 206,462 unique concepts and 2.5 million concept pairs.`;
      const tableLines = this.pdf.splitTextToSize(
        cleanText(tableText),
        this.pageWidth - 2 * this.margin
      );
      tableLines.forEach((line) => {
        this.pdf.text(line, this.margin, this.currentY);
        this.currentY += 4;
      });

      this.currentY += 4;

      this.setTextStyle(9, this.secondaryColor, "italic");
      this.addText(
        "Download from Huggingface: https://huggingface.co/datasets/Reacubeth/Quintuple",
        9
      );
      this.currentY += 2;

      this.setTextStyle(9, this.secondaryColor, "italic");
      this.addText("Download with git:", 9);
      this.currentY += 2;

      this.setTextStyle(8, this.textColor, "normal");
      this.addText(
        "git clone https://huggingface.co/datasets/Reacubeth/Co-occurrenceCitationQuintuple",
        8
      );
      this.currentY += 8;

      // 检查是否需要分页
      this.checkPageBreak(100);

      // 添加Ethics Statement
      this.setTextStyle(14, this.primaryColor, "bold");
      this.addText("Ethics Statement", 14);
      this.currentY += 6;

      this.setTextStyle(10, this.textColor, "normal");
      const ethicsText = `The datasets used in our research are collected through open-source approaches. The whole process is conducted legally, following ethical requirements. As for the Turing Test in our study, all participants are well informed about the purpose of experiments and the usage of test data, and we would not leak out or invade their privacy.`;

      const ethicsLines = this.pdf.splitTextToSize(
        cleanText(ethicsText),
        this.pageWidth - 2 * this.margin
      );
      ethicsLines.forEach((line) => {
        this.pdf.text(line, this.margin, this.currentY);
        this.currentY += 4;
      });

      this.currentY += 4;

      const ethicsText2 = `We see opportunities for researchers to apply the system to idea discovery, especially for interdisciplinary jobs. We encourage users to explore different combinations of subjects with the help of our system, making the most of its knowledge storage and thus maximizing the exploration ability of the system.`;
      const ethicsLines2 = this.pdf.splitTextToSize(
        cleanText(ethicsText2),
        this.pageWidth - 2 * this.margin
      );
      ethicsLines2.forEach((line) => {
        this.pdf.text(line, this.margin, this.currentY);
        this.currentY += 4;
      });

      this.currentY += 4;

      const ethicsText3 = `The main focus of the system is to provide a possible direction for future research, but the effect of human researchers will never be neglected. The massive data from various disciplines behind the system makes it capable of viewing the knowledge of an area in a multi-dimensional perspective and thus helps promote the development of novel interdisciplinary. However, considering the risks of misinformation generated by NLP tools, the verbalization only contains possible insights into new ideas. Researchers must thoroughly consider whether an idea is feasible or leads to adverse societal effects.`;
      const ethicsLines3 = this.pdf.splitTextToSize(
        cleanText(ethicsText3),
        this.pageWidth - 2 * this.margin
      );
      ethicsLines3.forEach((line) => {
        this.pdf.text(line, this.margin, this.currentY);
        this.currentY += 4;
      });

      this.currentY += 6;

      // 添加Acknowledgements
      this.setTextStyle(14, this.primaryColor, "bold");
      this.addText("Acknowledgements", 14);
      this.currentY += 6;

      this.setTextStyle(10, this.textColor, "normal");
      const ackText = `We would like to express our deepest gratitude to the scientists involved in the Deep-time Digital Earth program, whose contributions have been incredibly valuable. Additionally, we extend our thanks to Zhongmou He, Jia Guo, Zijun Di, Pandong Ma, Shengling Zhu, Yanpeng Li, Qi Li, Jiaxin Ding and Tao Shi from the IIOT Research Center at Shanghai Jiao Tong University for their unwavering support during the development of our system. This work was supported by NSF China (No.42050105, 62020106005, 62061146002, 61960206002), Shanghai Pilot Program for Basic Research - Shanghai Jiao Tong University.`;

      const ackLines = this.pdf.splitTextToSize(
        cleanText(ackText),
        this.pageWidth - 2 * this.margin
      );
      ackLines.forEach((line) => {
        this.pdf.text(line, this.margin, this.currentY);
        this.currentY += 4;
      });

      this.currentY += 8;

      // 添加Citation
      this.setTextStyle(14, this.primaryColor, "bold");
      this.addText("Citation", 14);
      this.currentY += 6;

      this.setTextStyle(10, this.textColor, "normal");
      const citationText = `If you use our work in your research or publication, please cite us as follows:`;
      const citationLines = this.pdf.splitTextToSize(
        cleanText(citationText),
        this.pageWidth - 2 * this.margin
      );
      citationLines.forEach((line) => {
        this.pdf.text(line, this.margin, this.currentY);
        this.currentY += 4;
      });

      this.currentY += 6;

      // 第一篇论文引用
      this.setTextStyle(9, this.secondaryColor, "italic");
      const citation1 = `@inproceedings{xu2023exploring,
  title={Exploring and Verbalizing Academic Ideas by Concept Co-occurrence},
  author={Xu, Yi and Sheng, Shuqian and Xue, Bo and Fu, Luoyi and Wang, Xinbing and Zhou, Chenghu},
  booktitle={Proceedings of the 61st Annual Meeting of the Association for Computational Linguistics (ACL)},
  year={2023}
}`;

      const citation1Lines = this.pdf.splitTextToSize(
        cleanText(citation1),
        this.pageWidth - 2 * this.margin - 10
      );
      citation1Lines.forEach((line) => {
        this.pdf.text(line, this.margin + 10, this.currentY);
        this.currentY += 3;
      });

      this.currentY += 4;

      // 第二篇论文引用
      const citation2 = `@inproceedings{xu2025deepreport,
  title={DeepReport: An AI-assisted Idea Generation System for Scientific Research},
  author={Xu, Yi and Fu, Luoyi and Sheng, Shuqian and Xue, Bo and Ding, Jiaxin and Zhou, Lei and Wang, Xinbing and Zhou, Chenghu},
  booktitle={ACM International Conference on Research and Development in Information Retrieval (SIGIR)},
  year={2025}
}`;

      const citation2Lines = this.pdf.splitTextToSize(
        cleanText(citation2),
        this.pageWidth - 2 * this.margin - 10
      );
      citation2Lines.forEach((line) => {
        this.pdf.text(line, this.margin + 10, this.currentY);
        this.currentY += 3;
      });

      this.currentY += 8;

      // 添加感谢信息
      this.setTextStyle(10, this.textColor, "normal");
      const thanksText = `Please let us know if you have any questions or feedback. Thank you for your interest in our work!`;
      const thanksLines = this.pdf.splitTextToSize(
        cleanText(thanksText),
        this.pageWidth - 2 * this.margin
      );
      thanksLines.forEach((line) => {
        this.pdf.text(line, this.margin, this.currentY);
        this.currentY += 4;
      });
    } catch (error) {
      console.warn("Failed to add DeepReport introduction section:", error);
    }

    // 添加所有页面的页脚
    const totalPages = this.pdf.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      this.pdf.setPage(i);
      this.addFooter(i);
    }

    // 返回PDF blob
    return this.pdf.output("blob");
  }

  // 保存PDF
  savePDF(blob, filename = "deepreport-results.pdf") {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  // 添加logo到第一页 - 与标题同一行
  async addLogoToFirstPage() {
    try {
      // 使用public目录下的PNG格式logo文件
      const logoPath = "/deepreport_logo.png";

      // 使用fetch获取PNG图片并转换为base64
      const response = await fetch(logoPath);
      if (!response.ok) {
        console.warn("Failed to fetch logo file:", logoPath);
        return;
      }

      const logoBlob = await response.blob();

      // 将blob转换为base64
      const base64Logo = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(logoBlob);
      });

      // 先加载图片获取原始尺寸
      const img = new Image();
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = base64Logo;
      });

      // 计算合适的logo尺寸，保持纵横比
      const maxLogoHeight = 25; // 最大高度 - 调小
      const aspectRatio = img.width / img.height;
      const logoHeight = Math.min(maxLogoHeight, img.height);
      const logoWidth = logoHeight * aspectRatio;

      // 计算logo位置 - 居中显示
      const x = (this.pageWidth - logoWidth) / 2; // 水平居中
      const y = this.margin + 5; // 距离顶部边距5单位

      // 添加logo到PDF
      this.pdf.addImage(base64Logo, "PNG", x, y, logoWidth, logoHeight);
    } catch (error) {
      console.warn("Failed to add logo to PDF:", error);
      // 如果添加logo失败，继续正常流程
    }
  }

  // 生成并保存PDF
  async generateAndSave(data, filename = "deepreport-results.pdf") {
    try {
      const blob = await this.generateReport(data);
      this.savePDF(blob, filename);
      return true;
    } catch (error) {
      console.error("PDF generation failed:", error);
      throw error;
    }
  }
}
